import {createFileRoute, useNavigate} from "@tanstack/react-router";
import {OttopilotColumnType} from "~/components/ottopilotDataViewer/column";
import {OttopilotDataViewer} from "~/components/ottopilotDataViewer/ottopilotDataViewer";
import {useOttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {TableOnlyPage} from "~/components/tableOnlyPage";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {useWorkspaceUserTemplates as useUserTemplates} from "~/contexts/workspaceContext";
import type {OttopilotContest} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotContestsWorkspaceCapability/domain/OttopilotContest";
import {contestStatusToDisplayName} from "~/utilities/domainStatusDisplayNames";
import {AnankeLinkButton} from "@vesta/ananke/components/anankeLinkButton";
import {getContests, getContests_Length} from "~/utilities/apis/api";
import {formatDateTime, getUserDisplayName} from "~/utilities/utilities";
import {uniqueUuid} from "@vesta/unique/unique";
import {ViewType} from "~/components/ottopilotDataViewer/view";

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/contests/",
)({
    loader: async () => {},
    component: RouteComponent,
});

function RouteComponent() {
    const {workspace} = useWorkspace();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {heimdallUser} = useWorkspaceUser();
    const users = useUsers();
    const userTemplates = useUserTemplates();

    const contestsDataViewerController = useOttopilotDataViewerController<OttopilotContest>({
        rowDisplayNameVariants: {
            singularSentenceCase: "OttopilotContest",
            pluralSentenceCase: "Contests",
            singularLowerCase: "contest",
            pluralLowerCase: "contests",
        },
        queryKeyPrefix: [uniqueUuid`50953b4e-4e01-4182-9ede-b69e7960a797`],
        rowsQueryFn: async ({filterConfiguration, limit, offset, sortConfiguration, searchConfiguration}) => {
            const contestsResult = await getContests({
                workspaceId: workspace.id,
                offset: offset,
                limit: limit,
                filterConfiguration: filterConfiguration,
                sortConfiguration: sortConfiguration,
                searchConfiguration: searchConfiguration,
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (contestsResult.success !== true) {
                throw contestsResult.error;
            }
            const contests: Array<OttopilotContest> = contestsResult.data;

            return contests;
        },
        rowCountQueryFn: async ({filterConfiguration, searchConfiguration}) => {
            const contestsLengthResult = await getContests_Length({
                workspaceId: workspace.id,
                filterConfiguration: filterConfiguration,
                searchConfiguration: searchConfiguration,
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (contestsLengthResult.success !== true) {
                throw contestsLengthResult.error;
            }
            const contestsLength = contestsLengthResult.data;

            return contestsLength;
        },
        columnValuesQueryFn: async () => {
            return [];
        },
        columns: {
            revisionId: uniqueUuid`e0b0e000-a069-4ccf-ad3a-2de96241842e`,
            lineItems: [
                {
                    id: "id",
                    type: OttopilotColumnType.uuid,
                    displayName: "ID",
                    valueGetter: (contest) => contest.id,
                },
                {
                    id: "createdBy",
                    type: OttopilotColumnType.uuid,
                    displayName: "Created by",
                    valueFormatter: (value) => getUserDisplayName(value, users, userTemplates),
                    valueGetter: (contest) => contest.createdBy,
                },
                {
                    id: "createdAt",
                    type: OttopilotColumnType.iso8601DateTime,
                    displayName: "Created at",
                    valueGetter: (contest) => contest.createdAt,
                    valueFormatter: (value) => formatDateTime(value),
                    sortingCapability: true,
                },
                {
                    id: "updatedBy",
                    type: OttopilotColumnType.uuid,
                    displayName: "Updated by",
                    valueGetter: (contest) => contest.updatedBy,
                    valueFormatter: (value) => getUserDisplayName(value, users, userTemplates),
                },
                {
                    id: "updatedAt",
                    type: OttopilotColumnType.iso8601DateTime,
                    displayName: "Updated at",
                    valueGetter: (contest) => contest.updatedAt,
                    valueFormatter: (value) => formatDateTime(value),
                    sortingCapability: true,
                },
                {
                    id: "title",
                    type: OttopilotColumnType.string,
                    displayName: "Title",
                    valueGetter: (contest) => contest.title,
                    sortingCapability: true,
                },
                {
                    id: "startDate",
                    type: OttopilotColumnType.iso8601Date,
                    displayName: "Start Date",
                    valueGetter: (contest) => contest.startDate,
                    sortingCapability: true,
                },
                {
                    id: "endDate",
                    type: OttopilotColumnType.iso8601Date,
                    displayName: "End Date",
                    valueGetter: (contest) => contest.endDate,
                    sortingCapability: true,
                },
                {
                    id: "status",
                    type: OttopilotColumnType.uuid,
                    displayName: "Status",
                    valueGetter: (contest) => contest.status as any,
                    valueFormatter: (value) => contestStatusToDisplayName(value as any),
                },
            ],
        },
        views: [
            {
                id: uniqueUuid`06151427-a976-4be8-a505-50cfe3b8d493`,
                viewType: ViewType.table,
                visibleColumns: ["id", "createdBy", "createdAt", "updatedBy", "updatedAt", "title", "startDate", "endDate", "status"],
                otherArgs: {
                    rowClass: "cursor-pointer",
                    onRowClicked: (event) => {
                        navigate({
                            to: `/w/${workspace.id}/ottopilot/contests/${event.data.id}`,
                        });
                    },
                },
            },
        ],
    });

    const navigate = useNavigate();

    return (
        <TableOnlyPage
            // breadcrumbs={["Journeys"]}
            title="Contests"
            titleRightSection={
                <AnankeLinkButton
                    // TODO: Somehow bring back the type safety here
                    href={`/w/${workspace.id}/ottopilot/contests/create`}
                >
                    + Add contest
                </AnankeLinkButton>
            }
            bottomSection={<OttopilotDataViewer controller={contestsDataViewerController} />}
        />
    );
}
